import {
    Paper,
    Grid,
    Tooltip,
    Typography,
    Divider,
    useTheme,
} from "@mui/material";
import { answerColorToThemeColor } from "components/Evaluation/Utils";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import { LLMResultDefinition } from "models/LLMs";
import React from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { useStandardConfigurationBuilder } from "../LLMConfigurationBuilderContext";
import { ResultDefinitionExample } from "./ResultDefinitionExample";
import { LabeledConversationsChip } from "../LabeledConversationsChip";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";
import { Error, Info } from "@mui/icons-material";

type LLMResultDefintionSectionProps = {
    llmResultDefinition: LLMResultDefinition;
};

export const ResultDefinitionSection = observer(function (
    props: LLMResultDefintionSectionProps,
) {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const builder = useStandardConfigurationBuilder();

    const theme = useTheme();
    const colors = answerColorToThemeColor(
        theme,
        props.llmResultDefinition.color,
    );

    const labeledConversations = store.labeledConversationCounts?.find(
        (tag) => tag.value === props.llmResultDefinition.result,
    );

    return (
        <Paper
            sx={(theme) => ({
                borderRadius: theme.shape.borderRadius / 2,
                border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                borderLeft: `4px solid ${colors?.borderColor}`,
                boxShadow: "0px 1px 2px 0px rgba(0, 0, 0, 0.07)",
            })}
        >
            <Grid container direction="column" flexWrap="nowrap">
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={2}
                    padding={2}
                    paddingBottom={1}
                >
                    <Grid
                        container
                        item
                        flexWrap="nowrap"
                        alignItems="center"
                        columnGap={1}
                    >
                        <Grid item>Result Tag:</Grid>
                        <Grid item>
                            <Tooltip title={props.llmResultDefinition.result}>
                                <Typography
                                    sx={(theme) => ({
                                        backgroundColor:
                                            colors?.backgroundColor,
                                        color: colors?.color,
                                        paddingBlock: "3px",
                                        paddingInline: theme.spacing(1),
                                        borderRadius:
                                            theme.shape.borderRadius / 4,
                                        width: "fit-content",
                                        maxWidth: "50dvw",
                                        textWrap: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        fontSize: "12px",
                                        fontWeight: 600,
                                    })}
                                >
                                    {props.llmResultDefinition.result}
                                </Typography>
                            </Tooltip>
                        </Grid>
                        {labeledConversations !== undefined &&
                            store.isAgreementTesting() && (
                                <React.Fragment>
                                    {labeledConversations.count <
                                        LLMClassifierBuilderStore.MinimumLabeledConversationsCount && (
                                        <Tooltip
                                            title={`This tag has ${
                                                labeledConversations.count
                                            } conversation${
                                                labeledConversations.count === 1
                                                    ? ""
                                                    : "s"
                                            } labeled. We recommend at least ${
                                                LLMClassifierBuilderStore.MinimumLabeledConversationsCount
                                            } labeled conversations for testing.`}
                                        >
                                            <Error color="error" />
                                        </Tooltip>
                                    )}
                                    {labeledConversations.count >=
                                        LLMClassifierBuilderStore.MinimumLabeledConversationsCount &&
                                        labeledConversations.count <
                                            LLMClassifierBuilderStore.OptimalLabeledConversationsCount && (
                                            <Tooltip
                                                title={`${LLMClassifierBuilderStore.OptimalLabeledConversationsCount} labeled conversations are recommended for optimal testing performance. This tag has ${labeledConversations.count} labeled conversations.`}
                                            >
                                                <Info color="primary" />
                                            </Tooltip>
                                        )}
                                </React.Fragment>
                            )}
                    </Grid>
                    <Grid item>
                        <AcxMainTextField
                            id={`result-${props.llmResultDefinition.result}-criteria`}
                            placeholderText="Enter criteria in plain text..."
                            fullWidth
                            disablePadding
                            multiline
                            multilinePadding
                            required
                            rowsMin={3}
                            labelText="Criteria"
                            error={
                                stepper.hasError.criteriaMissing &&
                                !props.llmResultDefinition.criteria
                            }
                            hideErrorIcon
                            value={props.llmResultDefinition.criteria}
                            onChange={(event) =>
                                builder.updateCriteria(
                                    props.llmResultDefinition,
                                    event.target.value,
                                )
                            }
                        />
                    </Grid>
                </Grid>
                <Divider />
                <Grid
                    container
                    item
                    direction="column"
                    flexWrap="nowrap"
                    rowGap={2}
                    padding={2}
                    paddingTop={1}
                >
                    {props.llmResultDefinition.examples.map((example, idx) => (
                        <Grid
                            item
                            key={`result-${props.llmResultDefinition.result}-example-${idx}`}
                        >
                            <ResultDefinitionExample
                                llmResultDefinition={props.llmResultDefinition}
                                example={example}
                                idx={idx}
                            />
                            {idx ===
                                props.llmResultDefinition.examples.length -
                                    1 && (
                                <AcxButton
                                    color="white"
                                    sx={(theme) => ({
                                        marginTop: theme.spacing(1),
                                        color: theme.palette.primary.main,
                                    })}
                                    onClick={() =>
                                        builder.addExample(
                                            props.llmResultDefinition,
                                        )
                                    }
                                >
                                    + Example
                                </AcxButton>
                            )}
                        </Grid>
                    ))}
                </Grid>
                {labeledConversations !== undefined && (
                    <>
                        <Divider />
                        <Grid item paddingInline={2} paddingBlock={1}>
                            <LabeledConversationsChip
                                result={props.llmResultDefinition.result}
                            />
                        </Grid>
                    </>
                )}
            </Grid>
        </Paper>
    );
});
